import React, { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { ScrollText, Filter, AlertTriangle } from "lucide-react";

// Surface every admin-relevant action. Uses a permissive substring match so
// both dotted actions ("admin.login") and underscore actions
// ("admin_password_changed") show up without a code edit for future ones.
const isRelevant = (action) => {
  if (!action) return false;
  const a = String(action).toLowerCase();
  return /^(admin|auth|user|profile|verification)([._]|$)/.test(a);
};

const TONE = {
  "admin.login":            "text-emerald-300 bg-emerald-500/10 border-emerald-500/30",
  "admin.login.failed":     "text-rose-300 bg-rose-500/10 border-rose-500/30",
  "admin.created":          "text-amber-300 bg-amber-500/10 border-amber-500/30",
  "admin.role.changed":     "text-amber-300 bg-amber-500/10 border-amber-500/30",
  "admin.suspended":        "text-rose-300 bg-rose-500/10 border-rose-500/30",
  "admin.active":           "text-emerald-300 bg-emerald-500/10 border-emerald-500/30",
  "admin.password.changed":       "text-amber-300 bg-amber-500/10 border-amber-500/30",
  "admin_password_changed":       "text-amber-300 bg-amber-500/10 border-amber-500/30",
  "admin_password_change_failed": "text-rose-300 bg-rose-500/10 border-rose-500/30",
  "verification.view":      "text-stone-300 bg-stone-800/60 border-stone-700",
};

export default function AdminAuditLogs() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState("");

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const { data } = await api.get("/admin/audit_logs", { params: { limit: 200 } });
      const logs = data.logs || data.audit_logs || [];
      setRows(logs.filter(l => isRelevant(l.action)));
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail;
      setError({ status, detail: typeof detail === "string" ? detail : String(detail || e.message || "Failed to load audit logs") });
      // eslint-disable-next-line no-console
      console.warn("[AdminAuditLogs] load:", status, detail);
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const filtered = filter
    ? rows.filter(l => (l.action || "").toLowerCase().includes(filter.toLowerCase()))
    : rows;

  return (
    <div>
      <p className="text-[10px] uppercase tracking-[0.22em] text-amber-400">Trust &amp; safety</p>
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-serif text-stone-50 mt-1">Audit logs</h1>
          <p className="text-sm text-stone-400 mt-1">Every admin login, role change, deactivation, password change and verification view.</p>
        </div>
        <div className="relative">
          <Filter className="w-4 h-4 text-stone-500 absolute left-3 top-3" />
          <input
            value={filter} onChange={(e) => setFilter(e.target.value)}
            placeholder="Filter by action…"
            className="pl-9 pr-3 h-10 rounded-lg bg-stone-900/60 border border-stone-700 text-sm text-stone-100 w-full sm:w-64"
            data-testid="adm-audit-filter"
          />
        </div>
      </div>

      {error && (
        <div className="mt-4 p-3 rounded-lg border border-rose-500/40 bg-rose-500/5 text-xs text-rose-200 flex items-start gap-2" data-testid="adm-audit-error">
          <AlertTriangle className="w-4 h-4 flex-none mt-0.5" />
          <div>
            <p className="font-medium">Could not load audit logs (HTTP {error.status ?? "?"})</p>
            <p className="text-rose-300/80 mt-0.5">{error.detail}</p>
          </div>
        </div>
      )}

      <div className="mt-5 rounded-xl border border-stone-800 bg-stone-950/50 overflow-x-auto" data-testid="adm-audit-table">
        <table className="w-full text-sm min-w-[720px]">
          <thead className="text-[11px] uppercase tracking-wider text-stone-400 bg-stone-900/40">
            <tr>
              <th className="px-4 py-3 text-left">Time</th>
              <th className="px-4 py-3 text-left">Actor</th>
              <th className="px-4 py-3 text-left">Action</th>
              <th className="px-4 py-3 text-left">Target</th>
              <th className="px-4 py-3 text-left">IP / UA</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-stone-800/70">
            {filtered.map(l => (
              <tr key={l.id || `${l.created_at}-${l.action}`} data-testid={`adm-audit-row-${l.id}`}>
                <td className="px-4 py-3 text-[11px] text-stone-500 whitespace-nowrap">
                  {l.created_at ? new Date(l.created_at).toLocaleString() : "—"}
                </td>
                <td className="px-4 py-3 text-[12px] text-stone-300">
                  {l.actor_id ? `${l.actor_id.slice(0,8)}… (${l.actor_role || "?"})` : <em className="text-stone-500">anon</em>}
                </td>
                <td className="px-4 py-3">
                  <span className={`inline-block px-2 py-0.5 rounded-full border text-[11px] ${TONE[l.action] || "text-stone-300 bg-stone-800/60 border-stone-700"}`}>
                    {l.action}
                  </span>
                </td>
                <td className="px-4 py-3 text-[11px] text-stone-400">
                  {l.entity_type ? `${l.entity_type}: ${(l.entity_id || "").slice(0,8)}…` : "—"}
                  {l.metadata && Object.keys(l.metadata).length > 0 && (
                    <div className="mt-1 text-[10px] text-stone-500 truncate max-w-[220px]">
                      {Object.entries(l.metadata).slice(0, 3).map(([k, v]) => `${k}: ${String(v)}`).join(" · ")}
                    </div>
                  )}
                </td>
                <td className="px-4 py-3 text-[10px] text-stone-500 whitespace-nowrap">
                  {l.ip_address || "—"}
                  {l.user_agent && <div className="truncate max-w-[160px]">{l.user_agent.slice(0, 40)}</div>}
                </td>
              </tr>
            ))}
            {!loading && !error && filtered.length === 0 && (
              <tr><td colSpan={5} className="p-8 text-center text-stone-500 text-sm">
                <ScrollText className="w-6 h-6 mx-auto mb-2 opacity-50" />
                No audit events yet.
              </td></tr>
            )}
            {loading && (
              <tr><td colSpan={5} className="p-8 text-center text-stone-500 text-sm">Loading…</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
